import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  MetricWidget,
  PieChartWidget,
  DonutChartWidget,
  BarChartWidget,
  GroupedBarChartWidget,
  StackedBarChartWidget,
  LineChartWidget,
  StackedLineChartWidget,
  TableChartWidget,
  TreeMapWidget,
  TimeseriesWidget,
  TextWidget,
} from '@common/components';
import * as styles from './WidgetRenderer.module.css';

const WIDGET_COMPONENTS = {
  metric: MetricWidget,
  pie: PieChartWidget,
  donut: DonutChartWidget,
  bar: BarChartWidget,
  'grouped-bar': GroupedBarChartWidget,
  'stacked-bar': StackedBarChartWidget,
  line: LineChartWidget,
  'stacked-line': StackedLineChartWidget,
  table: TableChartWidget,
  treemap: TreeMapWidget,
  timeseries: TimeseriesWidget,
  text: TextWidget,
};

function WidgetRenderer({ widget, dashboardArgs, dashboardFilters, isEditing = false, onEdit, onDelete }) {
  const { t } = useTranslation();
  const WidgetComponent = WIDGET_COMPONENTS[widget.type];

  // In edit mode, show placeholder instead of loading data
  const isEditModePlaceholder = widget.data === 'EDIT_MODE_PLACEHOLDER';

  if (!WidgetComponent) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>{widget.title}</h3>
          {isEditing && (
            <div className={styles.editActions}>
              <button
                type="button"
                className={styles.editButton}
                onClick={() => onEdit(widget)}
                title="Edit Widget"
              >
                ✏️
              </button>
              <button
                type="button"
                className={styles.deleteButton}
                onClick={() => onDelete(widget.id)}
                title="Delete Widget"
              >
                🗑️
              </button>
            </div>
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.error}>
            Unsupported widget type: {widget.type}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={`${styles.header} ${isEditing ? 'drag-handle' : ''}`}>
        <h3 className={styles.title}>{widget.title}</h3>
        {isEditing && (
          <div className={styles.editActions}>
            <button
              type="button"
              className={`${styles.editButton} react-grid-item-no-drag`}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onEdit(widget);
              }}
              title="Edit Widget"
            >
              ✏️
            </button>
            <button
              type="button"
              className={`${styles.deleteButton} react-grid-item-no-drag`}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(widget.id);
              }}
              title="Delete Widget"
            >
              🗑️
            </button>
          </div>
        )}
      </div>
      <div className={styles.content}>
        {isEditModePlaceholder ? (
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>📊</div>
            <div className={styles.placeholderText}>
              <strong>{widget.type.charAt(0).toUpperCase() + widget.type.slice(1).replace(/-/g, ' ')}</strong>
              <p>{t('dashboard.widgets.dataWillLoadInViewMode')}</p>
            </div>
          </div>
        ) : (
          <WidgetComponent
            widget={widget}
            dashboardArgs={dashboardArgs}
            dashboardFilters={dashboardFilters}
            isEditing={isEditing}
            // Enable row grouping for inventory data source
            // Groups consecutive identical values in the first column
            enableRowGrouping={
              widget.config?.dataSources?.some((ds) => ds.type === 'inventory')
            }
          />
        )}
      </div>
    </div>
  );
}

WidgetRenderer.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.string,
    ]),
    config: PropTypes.shape({}),
  }).isRequired,
  dashboardArgs: PropTypes.shape({}),
  dashboardFilters: PropTypes.shape({}),
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

WidgetRenderer.defaultProps = {
  dashboardArgs: {},
  dashboardFilters: {},
  isEditing: false,
  onEdit: null,
  onDelete: null,
};

export default WidgetRenderer;

